<template>
  <div class="comment-thread">
    <!-- Main comment -->
    <div class="hike-comments__comment-card comment-card-js-component" :style="{ marginLeft: indentLevel + 'px' }">
      <hr class="my-4">
      
      <div class="row">
        <div class="col-12 d-flex gap-3">
          <div class="square" style="width: 45px; height: 45px">
            <img loading="lazy" decoding="async" :alt="$t('comments.comment.avatar_alt', { name: comment.user.displayName })" class="rounded-circle mb-2 img-fluid"
                :src="comment.user.avatar.smUrl" />
          </div>
          <div class="d-flex justify-content-between w-100">
            <div class="d-flex flex-column justify-content-center">
              <p class="m-0">
                <a :href="publicProfileUrl" class="comment-user-link"><strong>{{ comment.user?.displayName }}</strong></a>
                <span> - </span>
                <span class="text-muted">{{ createdAt }}</span>
              </p>
              <CommentStarRating v-if="comment.rating" :rating="comment.rating" />
            </div>
            <div class="dropdown">
              <button v-if="ownedByUser" class="btn btn-secondary dropdown-toggle p-0" type="button" id="dropdownMenuButton" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false" style="width: 30px; height: 30px !important;">
              </button>
              <div class="dropdown-menu" aria-labelledby="dropdownMenuButton">
                <button class="dropdown-item btn btn-link btn-sm" @click="editComment">{{ $t('shared.edit') }}</button>
                <button class="dropdown-item btn btn-link btn-sm" @click="destroyComment">{{ $t('shared.delete') }}</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3" v-if="comment.body">
        <div class="col-12">
          <p :style="{ 'white-space': 'pre-line' }">{{ comment.body }}</p>
        </div>
      </div>

      <div class="row mt-2" v-if="comment.tags && comment.tags.length">
        <div class="col-12">
          <span v-for="tag in comment.tags" :key="tag" class="badge card text-white comment-tag mr-2 mb-2 ml-0 mt-0">{{ tagLabel(tag) }}</span>
        </div>
      </div>

      <div class="row mt-2 mb-2">
        <div class="col-sm-1 d-flex align-items-center">
          <CommentLikeable :likeable="comment" likeableType="Comment" :user="currentUser" />
        </div>
        <div class="col-sm-2" v-if="!atMaxDepth">
          <button class="btn btn-sm btn-link p-0" @click="toggleReply">
            <i class="bi bi-reply"></i> {{ $t('comments.comment_thread.reply') }}
          </button>
        </div>
        <div class="col-sm-4">
          <!-- Comments don't have recordings -->
        </div>
      </div>

      <CommentPhotos :review="comment" />

      <!-- Recording attachment -->
      <div v-if="comment.connectedRecording" class="row mt-3">
        <div class="col-12">
          <div class="recording-attachment bg-dark rounded p-3">
            <div class="d-flex align-items-center">
              <i class="bi bi-geo-alt-fill text-primary me-2"></i>
              <div>
                <strong>{{ comment.connectedRecording.title || $t('shared.recording') }}</strong>
                <div class="text-muted small">
                  {{ formatDistance(comment.connectedRecording.distanceMeters) }} · {{ formatDuration(comment.connectedRecording.elapsedSeconds) }}
                  <span v-if="comment.connectedRecording.user"> · {{ $t('comments.comment.by_display_name', { display_name: comment.connectedRecording.user.displayName }) }}</span>
                </div>
              </div>
              <a
                :href="`/recordings/users/${comment.connectedRecording.obfuscatedSlug}/map`"
                target="_blank"
                class="btn btn-sm btn-outline-primary ms-auto"
              >
                {{ $t('comments.comment_thread.view_recording') }}
              </a>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Child comments (recursive) -->
    <div v-if="showChildren && children.length > 0" class="ml-3 mt-3">
      <CommentThread
        v-for="childComment in children"
        :key="childComment.id"
        :comment="childComment"
        :depth="depth + 1"
        :max-depth="maxDepth"
        :current-user="currentUser"
        @reply-posted="$emit('reply-posted', $event)"
        @comment-updated="$emit('comment-updated', $event)"
        @comment-deleted="$emit('comment-deleted', $event)"
      />
    </div>

    <!-- Load more button for collapsed children -->
    <div v-if="hasChildren && !showChildren" class="ml-3 mt-2">
      <button class="btn btn-sm btn-link" @click="loadChildren">
        <span v-if="isLoadingChildren" class="spinner-border spinner-border-sm mr-1"></span>
        {{ isLoadingChildren
          ? $t('shared.loading')
          : childCount === 1
            ? $t('comments.comment_thread.show_one_reply')
            : $t('comments.comment_thread.show_replies', { count: childCount }) }}
      </button>
    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted } from 'vue'
import { formatCommentRecordingDistance } from '../../utils/helpers/commentRecordingDistance'
import moment from 'moment'
import CommentLikeable from './CommentLikeable.vue'
import CommentPhotos from './CommentPhotos.vue'
import CommentStarRating from './CommentStarRating.vue'
import { ratingTagLabel } from '../../utils/models/RatingTag'
import { i18n } from '../../i18n'

const t = (key, named = {}) => i18n.global.t(key, named)

export default {
  name: 'CommentThread',
  components: {
    CommentLikeable,
    CommentPhotos,
    CommentStarRating,
  },
  props: {
    comment: {
      type: Object,
      required: true
    },
    depth: {
      type: Number,
      default: 0
    },
    maxDepth: {
      type: Number,
      default: 4 // Keep UI aligned with backend MAX_NESTING_DEPTH
    },
    currentUser: {
      type: Object,
      required: true
    }
  },
  emits: ['reply-posted', 'comment-updated', 'comment-deleted'],
  setup(props, { emit }) {
    const showChildren = ref(false)
    const isLoadingChildren = ref(false)

    // Inject store if available
    const store = inject('store', null)

    const indentLevel = computed(() => {
      // Only indent after the first level, matching original ml-5 style
      return props.depth > 0 ? 20 : 0
    })

    const effectiveDepth = computed(() => {
      if (props.comment.depth !== undefined && props.comment.depth !== null) {
        return Number(props.comment.depth)
      }
      return props.depth
    })

    const atMaxDepth = computed(() => effectiveDepth.value >= props.maxDepth)

    const hasChildren = computed(() => {
      if (atMaxDepth.value) return false
      return (props.comment.childComments && props.comment.childComments.length > 0) ||
             props.comment.hasChildren ||
             props.comment.childCount > 0
    })

    const childCount = computed(() => {
      if (atMaxDepth.value) return 0
      return props.comment.childCount || (props.comment.childComments?.length || 0)
    })

    const children = computed(() => {
      if (atMaxDepth.value) return []
      return props.comment.childComments || []
    })

    const ownedByUser = computed(() => {
      return props.currentUser && props.comment.user &&
             props.currentUser.firebase_user_id === props.comment.user.firebaseUserId
    })

    const createdAt = computed(() => {
      return moment(props.comment.createdAt).fromNow()
    })

    const publicProfileUrl = computed(() => {
      const user = props.comment?.user || {}
      const firebaseId = user.firebaseUserId || user.firebase_user_id
      if (!firebaseId) return '#'
      return `/users/${firebaseId}/profile`
    })

    const loadChildren = () => {
      if (atMaxDepth.value) return
      // Simply toggle visibility since all data is already loaded
      showChildren.value = true
    }

    const toggleReply = () => {
      if (atMaxDepth.value) {
        return
      }
      if (!props.currentUser) {
        // Launch onboarding modal if user is not logged in
        if (window.onBoardingVue && typeof window.onBoardingVue.launchFor === 'function') {
          window.onBoardingVue.launchFor('Reviews')
        }
        return
      }
      // Open the comment modal in reply mode
      if (store) {
        store.dispatch('replyToComment', props.comment)
      }
    }

    const editComment = () => {
      if (store) {
        store.dispatch('editComment', props.comment)
      }
    }

    const destroyComment = () => {
      if (store) {
        store.dispatch('destroyComment', props.comment)
      } else {
        emit('comment-deleted', props.comment.id)
      }
    }

    const tagLabel = (tagKey) => ratingTagLabel(tagKey)

    const formatDistance = (meters) => formatCommentRecordingDistance(meters, t)

    const formatDuration = (seconds) => {
      if (!seconds) return t('comments.comment.duration_minutes', { minutes: 0 })
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      if (hours > 0) {
        return t('comments.comment.duration_hours_minutes', { hours, minutes })
      }
      return t('comments.comment.duration_minutes', { minutes })
    }

    // Auto-expand first two levels
    onMounted(() => {
      if (props.depth < 2 && hasChildren.value) {
        showChildren.value = true
      }
    })

    return {
      showChildren,
      children,
      isLoadingChildren,
      indentLevel,
      hasChildren,
      childCount,
      ownedByUser,
      createdAt,
      publicProfileUrl,
      loadChildren,
      toggleReply,
      editComment,
      destroyComment,
      tagLabel,
      formatDistance,
      formatDuration,
      atMaxDepth
    }
  }
}
</script>

<style scoped>
/* No custom styles - using Bootstrap dark theme classes only */
.recording-attachment {
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.dropdown-toggle::after{
  margin: 0;
}
</style>
